import { DomainUpsert } from "@/components/domain";
import { SelectSearch } from "@/components/select-search";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import { PhoneInput } from "@/components/ui/phone";
import { dateToString } from "@/lib/utils";
import { Countries, CreateGuide, Languages } from "@/models";
import { FormOptions } from "@deadcow-enterprises/react-forms";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export function Form({
  model,
  onUpsert,
}: {
  model?: CreateGuide;
  onUpsert: (data: CreateGuide) => Promise<void>;
}) {
  const { t } = useTranslation();
  const isEdit = useMemo(() => !!model, [model]);
  const formConfig = useMemo<FormOptions<CreateGuide>>(() => {
    const config: FormOptions<CreateGuide> = {
      name: {
        value: model?.name ?? "",
        validator: z.string().min(1, "errors.required"),
      },
      email: {
        value: model?.email ?? "",
        validator: z.string().email({
          message: "errors.emailFormat",
        }),
      },
      phone: {
        value: model?.phone,
      },
      birthdate: {
        value: model?.birthdate,
      },
      country: {
        value: model?.country,
      },
      language: {
        value: model?.language,
      },
      personId: {
        value: model?.personId ?? "",
      },
    };
    return config;
  }, [model]);

  return (
    <DomainUpsert
      title={model?.name ?? t("guides.new.title")}
      buttonTranslation={isEdit ? "guides.update.button" : "guides.new.button"}
      formConfig={formConfig}
      onUpsert={onUpsert}
      fields={[
        {
          key: "name",
          label: "guides.fields.name",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.name}
              onChange={(ev) => {
                form.setValue("name", ev.target.value);
              }}
            />
          ),
        },
        {
          key: "email",
          label: "guides.fields.email",
          input: (form, id) => (
            <Input
              id={id}
              inputMode="email"
              value={form.value.email}
              onChange={(ev) => {
                form.setValue("email", ev.target.value);
              }}
            />
          ),
        },
        {
          key: "phone",
          label: "guides.fields.phone",
          input: (form, id) => (
            <PhoneInput
              className="w-full"
              id={id}
              value={form.value.phone ?? ""}
              onChange={(value) => {
                form.setValue("phone", value ?? "");
              }}
            />
          ),
        },
        {
          key: "birthdate",
          label: "guides.fields.birthdate",
          input: (form, id) => (
            <DatePicker
              className="w-full"
              id={id}
              date={
                form.value.birthdate
                  ? new Date(form.value.birthdate)
                  : undefined
              }
              setDate={(d) =>
                form.setValue("birthdate", d ? dateToString(d) : undefined)
              }
            ></DatePicker>
          ),
        },
        {
          key: "country",
          label: "guides.fields.country",
          input: (form, id) => (
            <SelectSearch
              id={id}
              value={form.value.country ?? undefined}
              onChange={(v) => {
                form.setValue("country", v);
              }}
              options={
                Countries.map((o) => ({
                  value: o,
                  label: t(`countries.${o}`),
                })) ?? []
              }
            />
          ),
        },
        {
          key: "language",
          label: "guides.fields.language",
          input: (form, id) => (
            <MultiSelect
              id={id}
              selected={form.value.language ?? []}
              className="w-full"
              onChange={(v) => {
                form.setValue("language", v);
              }}
              options={
                Languages?.map((v) => ({
                  value: v,
                  label: v,
                })) ?? []
              }
            />
          ),
        },
      ]}
    />
  );
}
